import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, Circle } from 'lucide-react'
import { Step1, Step2, Step3, Step4, Step5, Step6 } from '../components/steps'
import { stepData } from '../data/frameworks'

const Demo = () => {
  const [currentStep, setCurrentStep] = useState(1)

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1)
    } else {
      setCurrentStep(1)
    }
  }

  const StepComponents = [Step1, Step2, Step3, Step4, Step5, Step6]
  const CurrentStepComponent = StepComponents[currentStep - 1]

  return (
    <div className="min-h-screen">
      {/* Progress Header */}
      <div className="sticky top-16 z-40 glass border-b border-surface-200 dark:border-surface-700">
        <div className="container-custom py-4">
          {/* Desktop Progress */}
          <div className="hidden md:flex items-center justify-between">
            {stepData.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className="flex items-center gap-3 group"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      currentStep === step.id
                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/30'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="font-semibold">{step.id}</span>
                    )}
                  </div>
                  <div className="hidden lg:block text-left">
                    <p
                      className={`text-sm font-medium transition-colors ${
                        currentStep === step.id
                          ? 'text-primary-500'
                          : 'text-surface-600 dark:text-surface-400'
                      }`}
                    >
                      {step.title}
                    </p>
                  </div>
                </button>
                {index < stepData.length - 1 && (
                  <div
                    className={`w-12 lg:w-20 h-0.5 mx-2 transition-colors ${
                      currentStep > step.id
                        ? 'bg-green-500'
                        : 'bg-surface-200 dark:bg-surface-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Mobile Progress */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-surface-600 dark:text-surface-400">
                Step {currentStep} of 6
              </span>
              <span className="text-sm font-medium text-primary-500">
                {stepData[currentStep - 1].title}
              </span>
            </div>
            <div className="flex gap-1">
              {stepData.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    currentStep >= step.id
                      ? 'bg-gradient-to-r from-primary-500 to-accent-500'
                      : 'bg-surface-200 dark:bg-surface-700'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="container-custom section-padding">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CurrentStepComponent onNext={handleNext} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Step Navigation Dots (Mobile) */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 md:hidden">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full glass shadow-lg">
          {stepData.map((step) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className="p-1"
            >
              {currentStep === step.id ? (
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-primary-500 to-accent-500" />
              ) : (
                <Circle
                  className={`w-3 h-3 ${
                    currentStep > step.id
                      ? 'text-green-500 fill-green-500'
                      : 'text-surface-400'
                  }`}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Demo
